import React, { useEffect, useMemo, useState } from 'react';
import styles from '../../styles/GestionInteresadosStyles/GestionInteresados.module.css';
import { actualizarInteresado } from '../../api/interesados';
import { getInstitutions } from '../../api/institutions';
import { getSecciones } from '../../api/secciones';
import { getPrograms } from '../../api/programs';
import { getCycles } from '../../api/ciclosEscolares';
import { getCampus } from '../../api/campus';

const ESTADO_OPTIONS = [
  { value: 'NUEVO', label: 'NUEVO' },
  { value: 'SEGUIMIENTO', label: 'SEGUIMIENTO' },
  { value: 'EN_ESPERA', label: 'EN ESPERA' },
  { value: 'EN_PROCESO', label: 'EN PROCESO' },
  { value: 'ACEPTADO', label: 'ACEPTADO' },
  { value: 'RECHAZADO', label: 'RECHAZADO' },
  { value: 'PERDIO_INTERES', label: 'PERDIÓ INTERÉS' },
  { value: 'MATRICULADO', label: 'MATRICULADO' },
  { value: 'INSCRITO', label: 'INSCRITO' },
];

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function buildFormData(prospect) {
  return {
    nombreCompleto: prospect.nombre || '',
    telefono: prospect.telefono || '',
    municipio: prospect.municipio || '',
    idInstitucion: prospect.idInstitucion || '',
    idSeccion: prospect._raw?.seccion_id || '',
    idPrograma: prospect.idPrograma || '',
    idCicloIngreso: prospect.idCicloIngreso || '',
    comoSeEntero: prospect.comoSeEntero || '',
    estado: prospect.estado || 'NUEVO',
    email: prospect.email || '',
    fechaNacimiento: prospect.fechaNacimiento || '',
    nivelEstudios: prospect.nivelEstudios || '',
    idCampus: prospect.idCampus || prospect._raw?.id_campus || '',
    institucionProcedencia: prospect.institucionProcedencia || '',
  };
}

function BasicInfoTab({ prospect, onUpdateEstado, onRefresh, forceEdit = false, onFooterActionsChange }) {
  const [formData, setFormData] = useState(buildFormData(prospect));
  const [isEditing, setIsEditing] = useState(forceEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const [instituciones, setInstituciones] = useState([]);
  const [campusOptions, setCampusOptions] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [programas, setProgramas] = useState([]);
  const [ciclos, setCiclos] = useState([]);

  useEffect(() => {
    setFormData(buildFormData(prospect));
  }, [prospect]);

  useEffect(() => {
    setIsEditing(forceEdit);
  }, [forceEdit]);

  useEffect(() => {
    let mounted = true;

    async function loadCatalogs() {
      try {
        const [inst, campus, sec, prog, cic] = await Promise.all([
          getInstitutions(),
          getCampus(),
          getSecciones(),
          getPrograms(),
          getCycles(),
        ]);

        if (!mounted) return;
        setInstituciones(Array.isArray(inst) ? inst : []);
        setCampusOptions(Array.isArray(campus) ? campus : []);
        setSecciones(Array.isArray(sec) ? sec : []);
        setProgramas(Array.isArray(prog) ? prog : []);
        setCiclos(Array.isArray(cic) ? cic : []);
      } catch (error) {
        if (mounted) {
          console.error('[BasicInfoTab] Error cargando catálogos:', error);
        }
      }
    }

    loadCatalogs();
    return () => {
      mounted = false;
    };
  }, []);

  const institucionesMap = useMemo(() => {
    const map = new Map();
    instituciones.forEach((inst) => map.set(String(inst.id), inst.name));
    return map;
  }, [instituciones]);

  const seccionesMap = useMemo(() => {
    const map = new Map();
    secciones.forEach((s) => map.set(String(s.id), s.nombre));
    return map;
  }, [secciones]);

  const campusMap = useMemo(() => {
    const map = new Map();
    campusOptions.forEach((campus) => map.set(String(campus.id), campus.nombre));
    return map;
  }, [campusOptions]);

  const programasMap = useMemo(() => {
    const map = new Map();
    programas.forEach((p) => map.set(String(p.id), p.name));
    return map;
  }, [programas]);

  // Programas filtrados por la sección seleccionada (la sección vive en el programa)
  const programasFiltrados = useMemo(() => {
    if (!formData.idSeccion) return programas;
    return programas.filter((p) => String(p.sectionId) === String(formData.idSeccion));
  }, [programas, formData.idSeccion]);

  const ciclosMap = useMemo(() => {
    const map = new Map();
    ciclos.forEach((c) => map.set(String(c.id), c.name || c.nombre_ciclo));
    return map;
  }, [ciclos]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Al cambiar la sección, invalidar el programa si ya no pertenece a ella
      if (field === 'idSeccion') {
        const prog = programas.find((p) => String(p.id) === String(prev.idPrograma));
        if (value && prog && String(prog.sectionId) !== String(value)) {
          next.idPrograma = '';
        }
      }
      return next;
    });
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      setSaveMessage(null);

      const originalEstado = prospect.estado;
      const changedEstado = formData.estado !== originalEstado;

      const toNullableInt = (value) => {
        if (value === '' || value === null || typeof value === 'undefined') return null;
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      };

      const updateData = {
        nombre_completo: formData.nombreCompleto,
        telefono: formData.telefono,
        municipio_residencia: formData.municipio,
        email: formData.email,
        fecha_nacimiento: formData.fechaNacimiento,
        nivel_estudios_actual: formData.nivelEstudios,
        institucion_procedencia: formData.institucionProcedencia,
        como_se_entero: formData.comoSeEntero,
        id_institucion: toNullableInt(formData.idInstitucion),
        id_campus: toNullableInt(formData.idCampus),
        id_programa: toNullableInt(formData.idPrograma),
        id_ciclo_ingreso: toNullableInt(formData.idCicloIngreso),
      };

      await actualizarInteresado(prospect.id, updateData);

      if (changedEstado && onUpdateEstado) {
        await onUpdateEstado(prospect.id, formData.estado, 'Cambio de estado desde edición de información básica');
      }

      setSaveMessage({ type: 'success', text: 'Cambios guardados exitosamente' });
      setIsEditing(false);
      if (onRefresh) {
        setTimeout(() => onRefresh(), 500);
      }
    } catch (error) {
      console.error('[BasicInfoTab] Error al guardar cambios:', error);
      setSaveMessage({ type: 'error', text: `Error al guardar: ${error.message}` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(buildFormData(prospect));
    setIsEditing(false);
    setSaveMessage(null);
  };

  useEffect(() => {
    if (!onFooterActionsChange) return undefined;

    if (isEditing) {
      onFooterActionsChange({
        showSave: true,
        saveLabel: isSaving ? 'GUARDANDO...' : 'GUARDAR CAMBIOS',
        onSave: handleSaveChanges,
        onCancel: handleCancel,
        disableSave: isSaving,
        disableCancel: isSaving,
      });
    } else {
      onFooterActionsChange(null);
    }

    return () => onFooterActionsChange(null);
  }, [handleSaveChanges, handleCancel, isEditing, isSaving, onFooterActionsChange]);

  return (
    <div className={styles.tabContent}>
      {saveMessage && (
        <div
          style={{
            padding: '12px',
            marginBottom: '16px',
            borderRadius: '4px',
            backgroundColor: saveMessage.type === 'success' ? '#d4edda' : '#f8d7da',
            color: saveMessage.type === 'success' ? '#155724' : '#721c24',
            border: `1px solid ${saveMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          }}
        >
          {saveMessage.text}
        </div>
      )}

      <div className={styles.formGrid}>
        <div className={styles.formColumn}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Nombre Completo</label>
            {isEditing ? (
              <input
                type="text"
                className={styles.inputField}
                value={formData.nombreCompleto}
                onChange={(e) => handleInputChange('nombreCompleto', e.target.value)}
              />
            ) : (
              <span className={styles.fieldValue}>{formData.nombreCompleto || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Teléfono</label>
            {isEditing ? (
              <input
                type="tel"
                className={styles.inputField}
                value={formData.telefono}
                onChange={(e) => handleInputChange('telefono', e.target.value)}
              />
            ) : (
              <span className={styles.fieldValue}>{formData.telefono || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Municipio de residencia</label>
            {isEditing ? (
              <input
                type="text"
                className={styles.inputField}
                value={formData.municipio}
                onChange={(e) => handleInputChange('municipio', e.target.value)}
              />
            ) : (
              <span className={styles.fieldValue}>{formData.municipio || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Institución</label>
            {isEditing ? (
              <select
                className={styles.selectField}
                value={formData.idInstitucion}
                onChange={(e) => handleInputChange('idInstitucion', e.target.value)}
              >
                <option value="">Selecciona institución</option>
                {instituciones.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.fieldValue}>{institucionesMap.get(String(formData.idInstitucion)) || prospect.institucion || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Sección</label>
            {isEditing ? (
              <select
                className={styles.selectField}
                value={formData.idSeccion}
                onChange={(e) => handleInputChange('idSeccion', e.target.value)}
              >
                <option value="">Selecciona sección</option>
                {secciones.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.fieldValue}>{seccionesMap.get(String(formData.idSeccion)) || prospect.seccion || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Ciclo escolar de ingreso deseado</label>
            {isEditing ? (
              <select
                className={styles.selectField}
                value={formData.idCicloIngreso}
                onChange={(e) => handleInputChange('idCicloIngreso', e.target.value)}
              >
                <option value="">Selecciona ciclo</option>
                {ciclos.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.nombre_ciclo}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.fieldValue}>{ciclosMap.get(String(formData.idCicloIngreso)) || prospect.cicloIngreso || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Cómo se enteró del programa</label>
            {isEditing ? (
              <input
                type="text"
                className={styles.inputField}
                value={formData.comoSeEntero}
                onChange={(e) => handleInputChange('comoSeEntero', e.target.value)}
              />
            ) : (
              <span className={styles.fieldValue}>{formData.comoSeEntero || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Estado</label>
            {isEditing ? (
              <select
                className={styles.selectField}
                value={formData.estado}
                onChange={(e) => handleInputChange('estado', e.target.value)}
              >
                {ESTADO_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.fieldValue}>{formData.estado || '—'}</span>
            )}
          </div>
        </div>

        <div className={styles.formColumn}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Email</label>
            {isEditing ? (
              <input
                type="email"
                className={styles.inputField}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            ) : (
              <span className={styles.fieldValue}>{formData.email || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Fecha de nacimiento</label>
            {isEditing ? (
              <input
                type="date"
                className={styles.inputField}
                value={formData.fechaNacimiento}
                onChange={(e) => handleInputChange('fechaNacimiento', e.target.value)}
              />
            ) : (
              <span className={styles.fieldValue}>{formData.fechaNacimiento || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Nivel de estudios actual</label>
            {isEditing ? (
              <input
                type="text"
                className={styles.inputField}
                value={formData.nivelEstudios}
                onChange={(e) => handleInputChange('nivelEstudios', e.target.value)}
              />
            ) : (
              <span className={styles.fieldValue}>{formData.nivelEstudios || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Campus de interés</label>
            {isEditing ? (
              <select
                className={styles.selectField}
                value={formData.idCampus}
                onChange={(e) => handleInputChange('idCampus', e.target.value)}
              >
                <option value="">Selecciona campus</option>
                {campusOptions.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.nombre}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.fieldValue}>{campusMap.get(String(formData.idCampus)) || prospect.campusInteres || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Programa de interés</label>
            {isEditing ? (
              <select
                className={styles.selectField}
                value={formData.idPrograma}
                onChange={(e) => handleInputChange('idPrograma', e.target.value)}
              >
                <option value="">Selecciona programa</option>
                {programasFiltrados.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.fieldValue}>{programasMap.get(String(formData.idPrograma)) || prospect.programaInteres || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Institución de procedencia</label>
            {isEditing ? (
              <input
                type="text"
                className={styles.inputField}
                value={formData.institucionProcedencia}
                onChange={(e) => handleInputChange('institucionProcedencia', e.target.value)}
              />
            ) : (
              <span className={styles.fieldValue}>{formData.institucionProcedencia || '—'}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Fecha de creación</label>
            <span className={styles.fieldValue}>{formatDateTime(prospect._raw?.fecha_creacion)}</span>
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>Registrado por</label>
            <span className={styles.fieldValue}>{prospect._raw?.usuario_creacion_nombre || '—'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BasicInfoTab;
